import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { fetchSongV2ById, deleteSongV2, SongV2 } from "./songV2Api";
import styles from "./SongV2Shared.module.css";
import FallingNotesStage from "../songsterr/components/FallingNotesStage";
import type { ScoreV2 } from "../songsterr/types";
import { buildTempoIndex } from "../songsterr/tempoUtils";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Clock,
  User,
  Zap,
  Music,
  Youtube,
  AlertCircle,
  Loader,
  Lock,
  Volume2,
  Target,
  Play,
  Pause,
} from "lucide-react";

export default function SongV2Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [song, setSong] = useState<SongV2 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  
  // NoteRain state
  const [posMs, setPosMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);

  useEffect(() => {
    if (id) {
      loadSong(id);
    }
  }, [id]);

  // Build score from song data
  const scoreData: ScoreV2 | null = useMemo(() => {
    if (!song?.dt || !song?.m) return null;
    
    // dt/m array'lerinden events oluştur
    const events: Array<{ t0: number; dt: number; m: number }> = [];
    let absMs = 0;
    for (let i = 0; i < song.dt.length; i++) {
      if (i === 0) {
        absMs = song.dt[0];
      } else {
        absMs += song.dt[i];
      }
      events.push({
        t0: absMs,
        dt: song.dt[i],
        m: song.m[i] || 0,
      });
    }
    
    return {
      ppq: 1,
      tempo_map: [{ tick: 0, bpm: 60000 }],
      events,
    };
  }, [song]);

  const tempoIndex = useMemo(
    () => (scoreData ? buildTempoIndex(scoreData.tempo_map ?? [], scoreData.ppq) : []),
    [scoreData]
  );

  // Calculate active mask for current position
  const activeMask = useMemo(() => {
    if (!scoreData) return 0;
    const hitWindow = song?.hit_ms || 80;
    for (const event of scoreData.events) {
      if (Math.abs(event.t0 - posMs) <= hitWindow) {
        return event.m;
      }
    }
    return 0;
  }, [scoreData, posMs, song?.hit_ms]);

  // Animation loop for NoteRain
  useEffect(() => {
    if (!isPlaying || !scoreData) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    lastTimeRef.current = performance.now();
    
    const loop = (currentTime: number) => {
      const delta = currentTime - lastTimeRef.current;
      lastTimeRef.current = currentTime;
      
      setPosMs((prev) => {
        const newPos = prev + delta;
        // Loop back to start if we reach the end
        const lastEvent = scoreData.events[scoreData.events.length - 1];
        if (lastEvent && newPos > lastEvent.t0 + 2000) {
          return 0;
        }
        return newPos;
      });
      
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying, scoreData]);

  const togglePlay = () => {
    if (!isPlaying) {
      lastTimeRef.current = performance.now();
    }
    setIsPlaying(!isPlaying);
  };

  const resetPosition = () => {
    setPosMs(0);
    setIsPlaying(false);
  };

  const loadSong = async (songId: string) => {
    try {
      setLoading(true);
      setError("");
      const data = await fetchSongV2ById(songId);
      setSong(data);
    } catch (error: any) {
      console.error("Failed to load song:", error);
      setError(error.response?.data?.message || "Şarkı yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!song) return;
    if (
      window.confirm(
        `"${song.title}" şarkısını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`
      )
    ) {
      try {
        await deleteSongV2(song.songv2_id);
        navigate("/songsv2");
      } catch (error: any) {
        console.error("Delete failed:", error);
        alert("Şarkı silinirken hata oluştu. Lütfen tekrar deneyin.");
      }
    }
  };

  const formatDuration = (ms: number) => {
    const totalSecs = Math.floor(ms / 1000);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Loader className={styles.loadingSpinner} size={48} />
          <span>Şarkı yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.cardBody} style={{ textAlign: "center" }}>
            <AlertCircle
              size={48}
              style={{ color: "#ef4444", marginBottom: "1rem" }}
            />
            <h3 style={{ color: "white", marginBottom: "0.5rem" }}>Hata Oluştu</h3>
            <p
              style={{
                color: "rgba(255, 255, 255, 0.8)",
                marginBottom: "1.5rem",
              }}
            >
              {error}
            </p>
            <Link to="/songsv2" className={styles.button}>
              <ArrowLeft size={16} />
              Listeye Dön
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!song) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.cardBody} style={{ textAlign: "center" }}>
            <AlertCircle
              size={48}
              style={{ color: "#ef4444", marginBottom: "1rem" }}
            />
            <h3 style={{ color: "white", marginBottom: "0.5rem" }}>
              Şarkı Bulunamadı
            </h3>
            <Link to="/songsv2" className={styles.button}>
              <ArrowLeft size={16} />
              Listeye Dön
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <Music className={styles.titleIcon} />
            Şarkı Detayı
          </h1>
          <p className={styles.subtitle}>{song.title}</p>
        </div>
        <div className={styles.headerActions}>
          <Link to="/songsv2" className={styles.secondaryButton}>
            <ArrowLeft size={16} />
            Geri Dön
          </Link>
          <Link
            to={`/songsv2/edit/${song.songv2_id}`}
            className={styles.primaryButton}
          >
            <Edit size={16} />
            Düzenle
          </Link>
          <button onClick={handleDelete} className={styles.dangerButton}>
            <Trash2 size={16} />
            Sil
          </button>
        </div>
      </div>

      {/* Main Info Card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <Music size={20} />
          <h2>Temel Bilgiler</h2>
          {song.is_locked && (
            <span
              className={styles.badge}
              style={{
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.2)",
                color: "#ef4444",
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
              }}
            >
              <Lock size={14} />
              Kilitli
            </span>
          )}
        </div>
        <div className={styles.cardBody}>
          <div className={styles.detailGrid}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Başlık:</span>
              <span className={styles.detailValue}>{song.title}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Sanatçı:</span>
              <span className={styles.detailValue}>
                <User size={14} style={{ marginRight: "0.25rem" }} />
                {song.artist}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>BPM:</span>
              <span className={styles.detailValue}>
                <Zap size={14} style={{ marginRight: "0.25rem" }} />
                {song.bpm}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Time Signature:</span>
              <span className={styles.detailValue}>{song.ts}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Süre:</span>
              <span className={styles.detailValue}>
                <Clock size={14} style={{ marginRight: "0.25rem" }} />
                {formatDuration(song.duration_ms)}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Version:</span>
              <span className={styles.detailValue}>v{song.v}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <Target size={20} />
          <h2>Zamanlama Ayarları</h2>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.detailGrid}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Sync Offset:</span>
              <span className={styles.detailValue}>{song.sync_ms} ms</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Lookahead:</span>
              <span className={styles.detailValue}>{song.lookahead_ms} ms</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Hit Window:</span>
              <span className={styles.detailValue}>{song.hit_ms} ms</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Calibrate Speed:</span>
              <span className={styles.detailValue}>{song.calibrate_speed}x</span>
            </div>
          </div>
        </div>
      </div>

      {/* YouTube Card */}
      {song.source && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <Youtube size={20} />
            <h2>YouTube Kaynağı</h2>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.youtubeEmbed}>
              <iframe
                width="100%"
                height="400"
                src={`https://www.youtube.com/embed/${song.source.video_id}`}
                title={song.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className={styles.detailGrid} style={{ marginTop: "1rem" }}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Video ID:</span>
                <span className={styles.detailValue}>{song.source.video_id}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>URL:</span>
                <a
                  href={song.source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  {song.source.url}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Note Rain - Full Width */}
      {scoreData && (
        <div
          style={{
            background: "#1e293b",
            border: "1px solid #334155",
            borderRadius: 16,
            padding: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
            <div style={{ color: "#94a3b8", fontWeight: 800, display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Music size={20} />
              Note Rain Preview
            </div>
            <div style={{ display: "flex", gap: "0.5rem", marginLeft: "auto" }}>
              <button
                onClick={togglePlay}
                style={{
                  borderRadius: 8,
                  background: isPlaying
                    ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                    : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "white",
                  fontWeight: 700,
                  padding: "0.5rem 1rem",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.35rem",
                }}
              >
                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={resetPosition}
                style={{
                  borderRadius: 8,
                  background: "#374151",
                  color: "#f1f5f9",
                  fontWeight: 700,
                  padding: "0.5rem 1rem",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Reset
              </button>
            </div>
            <div
              style={{
                color: "#64748b",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 12,
              }}
            >
              {Math.round(posMs)}ms / {song.duration_ms}ms
            </div>
          </div>
          <FallingNotesStage
            score={scoreData}
            tempoIndex={tempoIndex}
            posMs={posMs}
            activeMask={activeMask}
            lookaheadMs={song.lookahead_ms || 2200}
            hitWindowMs={Math.max(10, Math.min(180, (song.hit_ms || 80) * 0.35))}
            speed={1.0}
          />
        </div>
      )}

      {/* Lanes Card */}
      {song.lanes && song.lanes.length > 0 && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <Volume2 size={20} />
            <h2>Lanes ({song.lanes.length})</h2>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.lanesGrid}>
              {song.lanes.map((lane) => (
                <div key={lane.lane} className={styles.laneCard}>
                  <div className={styles.laneHeader}>
                    <span className={styles.laneNumber}>Lane {lane.lane}</span>
                    <span className={styles.laneName}>{lane.name}</span>
                  </div>
                  <div className={styles.laneMidi}>
                    <span className={styles.midiLabel}>MIDI Notes:</span>
                    <span className={styles.midiValues}>
                      {lane.midi_notes.join(", ")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Chart Info Card */}
      {song.chart && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <Music size={20} />
            <h2>Chart Bilgileri</h2>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Format:</span>
                <span className={styles.detailValue}>{song.chart.format}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>PPQ:</span>
                <span className={styles.detailValue}>{song.chart.ppq}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Tempo Map:</span>
                <span className={styles.detailValue}>
                  {song.chart.tempo_map?.length || 0} entries
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Events:</span>
                <span className={styles.detailValue}>
                  {song.chart.events?.length || 0} events
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Metadata Card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <Clock size={20} />
          <h2>Metadata</h2>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.detailGrid}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Song ID:</span>
              <span className={styles.detailValue} style={{ fontFamily: "monospace" }}>
                {song.songv2_id}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Oluşturulma:</span>
              <span className={styles.detailValue}>
                {song.created_at
                  ? new Date(song.created_at).toLocaleString("tr-TR")
                  : "Bilinmiyor"}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Güncellenme:</span>
              <span className={styles.detailValue}>
                {song.updated_at
                  ? new Date(song.updated_at).toLocaleString("tr-TR")
                  : "Bilinmiyor"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
